import {Component, inject, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatOptionModule} from "@angular/material/core"
import {MatInputModule} from "@angular/material/input"
import {MatMenu, MatMenuItem, MatMenuModule} from "@angular/material/menu"
import {MatSelectModule} from "@angular/material/select"
import {RouterOutlet} from "@angular/router"
import {ContentTypeModel, MutationUpdateAssetInput} from "@generated"
import {AssetDetailsFragment} from "@app/platform/components/assets/asset-details/asset-details.generated"
import {ButtonComponent} from "@common/components/buttons/button/button.component"
import {CopyLegacyIdButtonComponent} from "@common/components/buttons/copy-legacy-id-button/copy-legacy-id-button.component"
import {RoutedDialogComponent} from "@common/components/dialogs/routed-dialog/routed-dialog.component"
import {EntityAddCardComponent} from "@common/components/entity/entity-add-card/entity-add-card.component"
import {EntityCardComponent} from "@common/components/entity/entity-card/entity-card.component"
import {OrganizationSelectComponent} from "@common/components/inputs/select/organization-select/organization-select.component"
import {SectionComponent} from "@common/components/item/item-details/section/section.component"
import {IsLoadingDirective} from "@common/directives"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateReturningDataAndErrors, mutateThrowingErrors} from "@common/helpers/api/mutate"
import {DialogSize} from "@common/models/dialogs"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {
    AssetDetailsGQL,
    CreateAssetDetailsMaterialAssignmentGQL,
    DeleteAssetDetailsGQL,
    DeleteAssetDetailsMaterialAssignmentGQL,
    UpdateAssetDetailsGQL,
} from "@platform/components/assets/asset-details/asset-details.generated"
import {BaseDetailsComponent} from "@platform/components/base/base-details/base-details.component"
import {ActivitySectionComponent} from "@platform/components/details/activity-section/activity-section.component"
import {ArticleIdSectionComponent} from "@platform/components/details/article-id-section/article-id-section.component"
import {CommentSectionComponent} from "@platform/components/details/comment-section/comment-section.component"
import {FilesSectionComponent} from "@platform/components/details/files-section/files-section.component"
import {GalleryImageComponent} from "@platform/components/details/gallery-image/gallery-image.component"
import {ProjectManagementTasksSectionComponent} from "@platform/components/details/project-management-tasks-section/project-management-tasks-section.component"
import {ShowPicturesButtonComponent} from "@platform/components/details/show-pictures-button/show-pictures-button.component"
import {TitleSectionComponent} from "@platform/components/details/title-section/title-section.component"
import {CornersLayoutComponent} from "@platform/components/layouts/corners-layout/corners-layout.component"
import {DetailsDialogLayoutComponent} from "@platform/components/layouts/details-dialog-layout/details-dialog-layout.component"
import {SelectMaterialComponent} from "@platform/components/materials/select-material/select-material.component"
import {StateLabelComponent} from "@platform/components/shared/state-label/state-label.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-asset-details",
    templateUrl: "./asset-details.component.html",
    styleUrls: ["./asset-details.component.scss"],
    imports: [
        DetailsDialogLayoutComponent,
        SectionComponent,
        GalleryImageComponent,
        EntityCardComponent,
        StateLabelComponent,
        EntityAddCardComponent,
        ButtonComponent,
        MatInputModule,
        IsLoadingDirective,
        FormsModule,
        MatOptionModule,
        MatSelectModule,
        RoutedDialogComponent,
        MatMenu,
        MatMenuItem,
        MatMenuModule,
        CornersLayoutComponent,
        CopyLegacyIdButtonComponent,
        CommentSectionComponent,
        FilesSectionComponent,
        ActivitySectionComponent,
        ProjectManagementTasksSectionComponent,
        ArticleIdSectionComponent,
        OrganizationSelectComponent,
        TitleSectionComponent,
        ShowPicturesButtonComponent,
        RouterOutlet,
    ],
})
export class AssetDetailsComponent extends BaseDetailsComponent<AssetDetailsFragment, Omit<MutationUpdateAssetInput, "id">> {
    override _contentTypeModel = ContentTypeModel.Asset
    override _fetchItem = fetchThrowingErrors(inject(AssetDetailsGQL))
    override _updateItem = mutateThrowingErrors(inject(UpdateAssetDetailsGQL))
    override _deleteItem = mutateReturningDataAndErrors(inject(DeleteAssetDetailsGQL))

    readonly createAssetDetailsMaterialAssignment = inject(CreateAssetDetailsMaterialAssignmentGQL)
    readonly deleteAssetDetailsMaterialAssignment = inject(DeleteAssetDetailsMaterialAssignmentGQL)

    override displayName = "asset"

    readonly organizations = inject(OrganizationsService)

    openSelectMaterialDialog = () => {
        this.dialog.selectInDialog(SelectMaterialComponent, {
            onSelect: (material) => {
                const assetId = this.$item()?.id
                if (assetId) {
                    void this.notifications.withUserFeedback(
                        async () => {
                            await mutateThrowingErrors(this.createAssetDetailsMaterialAssignment)({
                                input: {
                                    assetId,
                                    materialId: material.id,
                                },
                            })
                            this.refresh.item(this.$item())
                        },
                        {
                            success: "Material assignment created",
                            error: "Failed to create material assignment",
                        },
                    )
                }
            },
        })
    }

    deleteMaterialAssignment = (materialAssignment: {id: string}) => {
        void this.notifications.withUserFeedback(
            async () => {
                await mutateThrowingErrors(this.deleteAssetDetailsMaterialAssignment)({id: materialAssignment.id})
                this.refresh.item(this.$item())
            },
            {
                success: "Material assignment deleted",
                error: "Failed to delete material assignment",
            },
        )
    }

    protected readonly DialogSize = DialogSize
}
